<template>
  <ToolShell
    tool-name="Estimate template"
    h1="Free estimate template for Excel and Sheets."
    lede="An estimate tells a client what the work should cost before it starts, with a valid-until date. Add your business name, set that date, and download a clean estimate as a CSV that opens directly in Excel or Google Sheets. Free, instant, no signup."
    :trust="['No signup', 'Instant download', 'Works in Excel & Google Sheets']"
    :upsell-points="[
      'Clients approve the proposal online — no PDF back-and-forth',
      'Approved proposals convert to invoices in one click',
      'Payment links and follow-up reminders built in',
    ]"
    :free-bullets="[
      'A clean CSV estimate for Excel or Sheets',
      'You send it, track it, and follow up yourself',
      'Approval happens over email, in a thread somewhere',
      'The invoice is a separate document you build later',
    ]"
    slug="/tools/estimate-template"
    :meta-description="metaDescription"
    :doc-brand="business || 'Your business name'"
    :doc-subtitle="`Prepared for ${client || 'Client name'}`"
    doc-kind="ESTIMATE"
    stamp="Template"
  >
    <template #form>
      <label class="ta-field">
        <span>Your business name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M2 14V4.5L8 2l6 2.5V14M5.5 14v-4h5v4M2 14h12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <input v-model.trim="business" type="text" placeholder="Acme Studio LLC" />
        </div>
      </label>

      <label class="ta-field">
        <span>Client name</span>
        <div class="ta-input">
          <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="5.2" r="2.7" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 14c.7-2.6 2.7-4 5.2-4s4.5 1.4 5.2 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
          <input v-model.trim="client" type="text" placeholder="Client Co" />
        </div>
      </label>

      <div class="ta-field">
        <span id="estimate-valid-label">Valid for</span>
        <ToolDropdown
          v-model="validDays"
          :options="validOptions"
          aria-label="Valid for"
          labelledby="estimate-valid-label"
        />
      </div>

      <label class="ta-field">
        <span>Currency symbol</span>
        <div class="ta-input ta-input-sm">
          <input v-model.trim="currency" type="text" maxlength="3" placeholder="$" />
        </div>
      </label>

      <button type="button" class="ta-download" @click="download">
        Download CSV for Excel
        <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v9m0 0 4-4m-4 4L4 7M2.5 13.5h11" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <p class="ta-note">
        Opens in Excel and Google Sheets. When the client approves, turn the
        estimate into an invoice with the
        <NuxtLink :to="localePath('/tools/invoice-generator')">free invoice generator</NuxtLink>.
      </p>
    </template>

    <template #preview>
      <div class="ta-doc-meta">
        <div><span>Valid until</span><strong>{{ validUntilLabel }}</strong></div>
        <div><span>Estimate #</span><strong>EST-001</strong></div>
      </div>

      <div class="ta-table-scroll" tabindex="0">
        <table class="ta-table">
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Rate</th><th>Amount</th></tr>
          </thead>
          <tbody>
            <tr v-for="line in lines" :key="line[0]">
              <td>{{ line[0] }}</td>
              <td class="ta-num">{{ line[1] }}</td>
              <td class="ta-num">{{ currency }}{{ Number(line[2]).toLocaleString("en-US") }}</td>
              <td class="ta-num">{{ currency }}{{ (Number(line[1]) * Number(line[2])).toLocaleString("en-US") }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="ta-doc-total" :class="{ 'ta-doc-total--long': `${currency}${total.toLocaleString('en-US')}`.length > 11 }">
        <span>Estimated total</span>
        <strong>{{ currency }}{{ total.toLocaleString("en-US") }}</strong>
      </div>

      <p class="ta-doc-note">Estimate only — final invoice reflects the approved scope.</p>
    </template>

    <template #steps>
      <div><span>01</span><div><h2>Fill in two names</h2><p>Your business and the client — the document updates live as you type.</p></div></div>
      <div><span>02</span><div><h2>Pick how long it's valid</h2><p>Every good estimate expires. Tap 7, 14, 30, or 60 days and the date prints on the document.</p></div></div>
      <div><span>03</span><div><h2>Download the CSV</h2><p>One click, a clean file that opens in Excel and Google Sheets with the scope rows ready.</p></div></div>
    </template>

    <ToolGuide :sections="guideSections" faq-title="Estimate template questions" :faq="faq" />

    <RelatedPages path="/tools/estimate-template" title="Free estimate template" />
  </ToolShell>
</template>

<script setup>
import ToolDropdown from "~/components/tools/ToolDropdown.vue";
import ToolGuide from "~/components/tools/ToolGuide.vue";
import ToolShell from "~/components/tools/ToolShell.vue";
import { formatEstimateDate } from "~/utils/estimate-date.mjs";
import { csv } from "~/utils/client-work-tools.mjs";

const { localePath } = useWorkspaceLocale();

const business = ref("");
const client = ref("");
const currency = ref("$");
const validDays = ref(30);

const lines = [
  ["Site visit and scope", "1", "0"],
  ["Phase 1 — Preparation", "1", "800"],
  ["Phase 2 — Main work", "1", "2400"],
  ["Cleanup and handoff", "1", "300"],
];

const total = computed(() =>
  lines.reduce((sum, [, qty, rate]) => sum + Number(qty) * Number(rate), 0),
);

// Dates are client-only: prerendered HTML must stay identical day to day or
// the localization pipeline fails with "Rendered site copy changed".
const now = ref(null);
onMounted(() => {
  now.value = new Date();
});

const datePlusDays = (days) => {
  const d = new Date(now.value);
  d.setDate(d.getDate() + days);
  return d;
};

const validOptions = computed(() =>
  [7, 14, 30, 60].map((days) => ({
    value: days,
    label: `${days} days`,
    hint: now.value
      ? `until ${datePlusDays(days).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : "",
  })),
);

const validUntilDate = computed(() => (now.value ? datePlusDays(validDays.value) : null));

const validUntilLabel = computed(() =>
  validUntilDate.value
    ? validUntilDate.value.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "—",
);

const validUntilIso = computed(() =>
  validUntilDate.value ? formatEstimateDate(validUntilDate.value) : "",
);

const activeRows = computed(() => {
  const rows = [
    { kind: "is-head", cells: ["ESTIMATE"] },
    { kind: "", cells: ["From", business.value || "Your business name"] },
    { kind: "", cells: ["Prepared for", client.value || "Client name"] },
    {
      kind: "",
      cells: ["Valid until", validUntilIso.value, "Estimate #", "EST-001"],
    },
    { kind: "is-cols", cells: ["Description", "Qty", "Rate", "Amount"] },
  ];
  let sum = 0;
  for (const [desc, qty, rate] of lines) {
    const amount = Number(qty) * Number(rate);
    sum += amount;
    rows.push({
      kind: "",
      cells: [desc, qty, `${currency.value}${rate}`, `${currency.value}${amount.toLocaleString("en-US")}`],
    });
  }
  rows.push({ kind: "is-total", cells: ["Estimated total", "", "", `${currency.value}${sum.toLocaleString("en-US")}`] });
  rows.push({ kind: "", cells: ["Notes", "Estimate only — final invoice reflects the approved scope."] });
  return rows;
});

const download = () => {
  const blob = new Blob([csv(activeRows.value.map(row => row.cells))], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "estimate-template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const metaDescription =
  "Download a free estimate template as a CSV that opens in Excel or Google Sheets — with valid-until date and scope notes. Instant, no signup.";

const guideSections = [
  {
    title: "What goes in an estimate",
    paragraphs: [
      "An estimate tells a client what you expect the work to cost before it starts. A good one is short, specific and easy to approve. The template above covers the main parts, and you should add anything particular to the job.",
    ],
    bullets: [
      "Your business details and the client's name and contact.",
      "An estimate number and the date you prepared it.",
      "A valid-until date, so the price does not stay open forever.",
      "Scope lines, each with a description, quantity, rate and amount.",
      "Assumptions and exclusions, such as what you have not priced or what the client must provide.",
      "The estimated total, labelled as an estimate, and any deposit you will ask for.",
      "How to accept: a reply, a signature or a button, with the next step spelled out.",
    ],
  },
  {
    title: "Estimate, quote and invoice are different documents",
    paragraphs: [
      "An estimate is an approximate price for work that has not happened yet, and the final figure can move if the scope does. A quote states a firm price for a defined scope. An invoice comes later and asks for payment for work delivered or a milestone reached.",
      "Keep them apart. An estimate is not a bill, so it should never carry a due date, and the final invoice should reflect the scope the client approved. Where the work is well defined, a fixed quote or proposal is often clearer than an estimate.",
    ],
    links: [
      { label: "Invoice vs receipt", path: "/learn/invoice-vs-receipt" },
      { label: "How to write a proposal", path: "/learn/how-to-write-a-proposal" },
    ],
  },
  {
    title: "Choosing a validity date",
    paragraphs: [
      "Prices rise, schedules fill up and scopes drift, so every estimate should expire. Fourteen to thirty days is a common window for service work. Use a shorter one when your costs change quickly or your calendar is tight, and a longer one for larger projects where the client needs time to get internal approval.",
      "When the date arrives, follow up once: either extend it in writing or re-issue the estimate at the current price.",
    ],
  },
  {
    title: "From accepted estimate to deposit and invoice",
    paragraphs: [
      "Once the client says yes, turn the estimate into real commitments. Confirm the scope in writing, ask for the deposit you stated, and schedule the invoices that follow.",
    ],
    bullets: [
      "Record the approval, with the date and who approved it.",
      "Send a deposit invoice, referencing the accepted estimate.",
      "Invoice the balance at the milestones or date you agreed.",
      "If the work will cost more than estimated, tell the client in writing and get approval before you carry on.",
    ],
    links: [
      { label: "Free invoice generator", path: "/tools/invoice-generator" },
      { label: "Deposit calculator", path: "/tools/deposit-calculator" },
      { label: "Deposit invoice guide", path: "/learn/deposit-invoice" },
    ],
  },
  {
    title: "Proposals in Workspace369",
    paragraphs: [
      "In Workspace369 this job is done by Proposals, also called quotes. A client approves the proposal online, can pay a deposit on acceptance, and the approved proposal converts to an invoice. Deposits on acceptance and payment links are on every plan.",
    ],
    links: [{ label: "Proposals and quotes", path: "/features/quoting" }],
  },
];

const faq = [
  {
    q: "What is the difference between an estimate and a quote?",
    a: "An estimate is an approximate price that can change if the scope changes. A quote is a firm price for a defined scope. Use an estimate when the work is still being scoped, and a quote when you and the client both know exactly what is included.",
  },
  {
    q: "How long should an estimate be valid?",
    a: "Fourteen to thirty days suits most service work. Choose the shorter end if your costs or calendar change quickly. Print the valid-until date on the document so the client knows when the price needs to be confirmed again.",
  },
  {
    q: "What if the job costs more than I estimated?",
    a: "Tell the client as soon as you know, in writing, and explain what changed. Offer a revised estimate or a change to the scope, and get their approval before doing the extra work, so the final invoice holds no surprises.",
  },
];

useHead(() => ({
  title: "Free Estimate Template: Excel & Google Sheets | Workspace369",
  meta: [
    {
      name: "description",
      content: metaDescription,
    },
    { name: "twitter:title", content: "Free Estimate Template: Excel & Google Sheets | Workspace369" },
    {
      name: "twitter:description",
      content: "Download a free estimate template as a CSV that opens in Excel or Google Sheets. Instant, no signup.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Free Estimate Template: Excel & Google Sheets | Workspace369" },
    {
      property: "og:description",
      content: "Download a free estimate template as a CSV that opens in Excel or Google Sheets. Instant, no signup.",
    },
  ],
}));
</script>
